import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import EmployeeManagement from './pages/EmployeeManagement';
import DuplicateCleanupReport from './pages/DuplicateCleanupReport';
import DeletedEmployees from './pages/DeletedEmployees';
import AttendanceManagement from './pages/AttendanceManagement';
import LeaveManagement from './pages/LeaveManagement';
import PayrollManagement from './pages/PayrollManagement';
import PerformanceReviews from './pages/PerformanceReviews';
import DocumentManagement from './pages/DocumentManagement';
import Departments from './pages/Departments';
import EmployeeDashboard from './pages/EmployeeDashboard';
import Payslips from './pages/Payslips';
import ChangePassword from './pages/ChangePassword';
import CompanySettings from './pages/CompanySettings';
import IntegrationsSettings from './pages/IntegrationsSettings';
import BGVCases from './pages/BGVCases';
import BGVCandidatePortal from './pages/BGVCandidatePortal';
import EnhancedEmployeeManagement from './pages/EnhancedEmployeeManagement';
import StatutoryCompliance from './pages/StatutoryCompliance';
import OfferLetters from './pages/OfferLetters';
import OfferLettersSummary from './pages/OfferLettersSummary';
import OfferLetterTemplates from './pages/OfferLetterTemplates';
import TaxDeclaration from './pages/TaxDeclaration';
import Reimbursements from './pages/Reimbursements';
import AttendanceEditLogs from './pages/AttendanceEditLogs';
import AttendanceSummary from './pages/AttendanceSummary';
import FinalSettlement from './pages/FinalSettlement';
import AccountingDashboard from './pages/AccountingDashboard';
import AccountingMasterData from './pages/AccountingMasterData';
import AccountingTransactions from './pages/AccountingTransactions';
import AccountingReports from './pages/AccountingReports';
import AssetManagement from './pages/AssetManagement';
import MyAssets from './pages/MyAssets';
// Lazy load large components to avoid babel issues
const OrgChart = React.lazy(() => import('./pages/OrgChart'));
const MyProfile = React.lazy(() => import('./pages/MyProfile'));
import AccountingAdvancedMIS from './pages/AccountingAdvancedMIS';
import AccountsReceivable from './pages/AccountsReceivable';
import DataExport from './pages/DataExport';
import LeavePolicyManagement from './pages/LeavePolicyManagement';
import ExitManagement from './pages/ExitManagement';
import AccessRemoval from './pages/AccessRemoval';
import HRAnalytics from './pages/HRAnalytics';
import LoginAuditLog from './pages/LoginAuditLog';
import EmployeeLetters from './pages/EmployeeLetters';
import DocumentConsolidated from './pages/DocumentConsolidated';
import UserManagement from './pages/UserManagement';
import HealthMonitor from './pages/HealthMonitor';
import LeaveConflictSettings from './pages/LeaveConflictSettings';
import MyPerformanceDashboard from './pages/MyPerformanceDashboard';
import OpsManagerDashboard from './pages/OpsManagerDashboard';
import PPUSalary from './pages/PPUSalary';
import Permissions from './pages/Permissions';
import KudosWall from './pages/KudosWall';
import OnboardingPortal from './pages/OnboardingPortal';
import OnboardingAdmin from './pages/OnboardingAdmin';
import ProbationTracker from './pages/ProbationTracker';
import TrainingManagement from './pages/TrainingManagement';
import MyTraining from './pages/MyTraining';
import SkillsMatrix from './pages/SkillsMatrix';
import CertificateVerify from './pages/CertificateVerify';
// Inventory Management
import InventoryDashboard from './pages/inventory/InventoryDashboard';
import InventoryItemsList from './pages/inventory/InventoryItemsList';
import Merchants from './pages/inventory/Merchants';
import TransferInventory from './pages/inventory/TransferInventory';
import Returns from './pages/inventory/Returns';
import WarehousesAndCatalog from './pages/inventory/WarehousesAndCatalog';
import MovementHistory from './pages/inventory/MovementHistory';
import InventoryReports from './pages/inventory/InventoryReports';
import InventoryRequests from './pages/inventory/InventoryRequests';
import DistributionAnalytics from './pages/inventory/DistributionAnalytics';
import InventoryUsersAccess from './pages/inventory/InventoryUsersAccess';
import RecruiterPerformance from './pages/RecruiterPerformance';
import KycVerification from './pages/KycVerification';
import EmailLogs from './pages/EmailLogs';
import WhatsAppButton from './components/WhatsAppButton';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

function App() {
  const [user, setUser] = React.useState(null);
  const [isAuthLoading, setIsAuthLoading] = React.useState(true);

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (token && userData) {
      try {
        setUser(JSON.parse(userData));
      } catch (e) {
        // Invalid user data in localStorage, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
      }
    }
    setIsAuthLoading(false);
  }, []);

  const ProtectedRoute = ({ children }) => {
    // Show loading spinner while checking auth state
    if (isAuthLoading) {
      return (
        <div className="flex items-center justify-center h-screen bg-slate-50">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      );
    }
    if (!user) {
      return <Navigate to="/login" replace />;
    }
    return children;
  };

  return (
    <>
      <BrowserRouter>
        <ErrorBoundary>
          <Routes>
          <Route path="/login" element={<Login setUser={setUser} />} />
          <Route path="/onboarding/:token" element={<OnboardingPortal />} />
          <Route path="/certificate/:certificateId" element={<CertificateVerify />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Navigate to="/org-chart" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                {user?.role === 'employee' ? <EmployeeDashboard user={user} setUser={setUser} /> : <AdminDashboard user={user} setUser={setUser} />}
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees"
            element={
              <ProtectedRoute>
                <EmployeeManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/duplicate-cleanup"
            element={
              <ProtectedRoute>
                <DuplicateCleanupReport user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/deleted"
            element={
              <ProtectedRoute>
                <DeletedEmployees user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/org-chart"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div></div>}>
                  <OrgChart user={user} setUser={setUser} />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-profile"
            element={
              <ProtectedRoute>
                <React.Suspense fallback={<div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-indigo-500 border-t-transparent rounded-full"></div></div>}>
                  <MyProfile user={user} setUser={setUser} />
                </React.Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance"
            element={
              <ProtectedRoute>
                <AttendanceManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance-edit-logs"
            element={
              <ProtectedRoute>
                <AttendanceEditLogs user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance-summary"
            element={
              <ProtectedRoute>
                <AttendanceSummary user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/final-settlement"
            element={
              <ProtectedRoute>
                <FinalSettlement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leaves"
            element={
              <ProtectedRoute>
                <LeaveManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-policy"
            element={
              <ProtectedRoute>
                <LeavePolicyManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll"
            element={
              <ProtectedRoute>
                <PayrollManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statutory-compliance"
            element={
              <ProtectedRoute>
                <StatutoryCompliance user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviews"
            element={
              <ProtectedRoute>
                <PerformanceReviews user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-performance"
            element={
              <ProtectedRoute>
                <MyPerformanceDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ops-dashboard"
            element={
              <ProtectedRoute>
                <OpsManagerDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          {/* Inventory Management routes */}
          <Route path="/inventory" element={<ProtectedRoute><InventoryDashboard user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/items" element={<ProtectedRoute><InventoryItemsList user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/merchants" element={<ProtectedRoute><Merchants user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/transfer" element={<ProtectedRoute><TransferInventory user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/returns" element={<ProtectedRoute><Returns user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/warehouses" element={<ProtectedRoute><WarehousesAndCatalog user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/movements" element={<ProtectedRoute><MovementHistory user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/reports" element={<ProtectedRoute><InventoryReports user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/analytics" element={<ProtectedRoute><DistributionAnalytics user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/users" element={<ProtectedRoute><InventoryUsersAccess user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route path="/inventory/requests" element={<ProtectedRoute><InventoryRequests user={user} setUser={setUser} /></ProtectedRoute>} />
          <Route
            path="/ppu-salary"
            element={
              <ProtectedRoute>
                <PPUSalary user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kudos"
            element={
              <ProtectedRoute>
                <KudosWall user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <DocumentManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/departments"
            element={
              <ProtectedRoute>
                <Departments user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payslips"
            element={
              <ProtectedRoute>
                <Payslips user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tax-declaration"
            element={
              <ProtectedRoute>
                <TaxDeclaration user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute>
                <ChangePassword user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/company-settings"
            element={
              <ProtectedRoute>
                <CompanySettings user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/integrations-settings"
            element={
              <ProtectedRoute>
                <IntegrationsSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/bgv"
            element={
              <ProtectedRoute>
                <BGVCases />
              </ProtectedRoute>
            }
          />
          <Route path="/bgv-portal/:token" element={<BGVCandidatePortal />} />
          <Route
            path="/email-logs"
            element={
              <ProtectedRoute>
                <EmailLogs user={user} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute>
                <Permissions user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/user-management"
            element={
              <ProtectedRoute>
                <UserManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/enhanced-employees"
            element={
              <ProtectedRoute>
                <EnhancedEmployeeManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-letters"
            element={
              <ProtectedRoute>
                <OfferLetters user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-letters-summary"
            element={
              <ProtectedRoute>
                <OfferLettersSummary user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/offer-letter-templates"
            element={
              <ProtectedRoute>
                <OfferLetterTemplates user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reimbursements"
            element={
              <ProtectedRoute>
                <Reimbursements user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting"
            element={
              <ProtectedRoute>
                <AccountingDashboard user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/master-data"
            element={
              <ProtectedRoute>
                <AccountingMasterData user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/transactions"
            element={
              <ProtectedRoute>
                <AccountingTransactions user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/reports"
            element={
              <ProtectedRoute>
                <AccountingReports user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/advanced-mis"
            element={
              <ProtectedRoute>
                <AccountingAdvancedMIS user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/accounting/receivables"
            element={
              <ProtectedRoute>
                <AccountsReceivable user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-export"
            element={
              <ProtectedRoute>
                <DataExport user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/exit-management"
            element={
              <ProtectedRoute>
                <ExitManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/asset-management"
            element={
              <ProtectedRoute>
                <AssetManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-assets"
            element={
              <ProtectedRoute>
                <MyAssets user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/access-removal"
            element={
              <ProtectedRoute>
                <AccessRemoval user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/hr-analytics"
            element={
              <ProtectedRoute>
                <HRAnalytics user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/login-audit-log"
            element={
              <ProtectedRoute>
                <LoginAuditLog user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employee-letters"
            element={
              <ProtectedRoute>
                <EmployeeLetters user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents-consolidated"
            element={
              <ProtectedRoute>
                <DocumentConsolidated user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/health-monitor"
            element={
              <ProtectedRoute>
                <HealthMonitor user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave-conflict-settings"
            element={
              <ProtectedRoute>
                <LeaveConflictSettings user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/onboarding-admin"
            element={
              <ProtectedRoute>
                <OnboardingAdmin user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/probation-tracker"
            element={
              <ProtectedRoute>
                <ProbationTracker user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training"
            element={
              <ProtectedRoute>
                <TrainingManagement user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-training"
            element={
              <ProtectedRoute>
                <MyTraining user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/skills-matrix"
            element={
              <ProtectedRoute>
                <SkillsMatrix user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/recruiter-performance"
            element={
              <ProtectedRoute>
                <RecruiterPerformance user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kyc-verification"
            element={
              <ProtectedRoute>
                <KycVerification user={user} setUser={setUser} />
              </ProtectedRoute>
            }
          />
        </Routes>
        <WhatsAppButton user={user} />
        </ErrorBoundary>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;